import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Shield, Eye, Flame, Zap, Wind } from 'lucide-react';
import { IMAGES, bgStyle } from '@/config/images';

const SafetyDisclaimer = () => {
  return (
    <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" style={bgStyle(IMAGES.SAFETY_DISCLAIMER)}>
      <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
      <div className="max-w-4xl mx-auto">
        {/* Back Link */}
        <Link to="/" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-red-500/20 rounded-lg">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-red-500 mb-1">IMPORTANT</div>
              <h1 
                className="text-3xl md:text-4xl font-bold text-red-500 tracking-tight uppercase"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Safety Disclaimer
              </h1>
            </div>
          </div>
        </div>

        {/* Warning Banner */}
        <div className="bg-red-500/10 border-2 border-red-500/50 rounded-lg p-6 mb-8">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-8 h-8 text-red-500 flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-xl font-bold text-red-400 mb-2">Read Before Using CNC Equipment</h2>
              <p className="text-zinc-300">
                The information, tools, calculators, and instructional content provided on CNC-Helpdesk.com ("the Service") are intended <strong>solely for educational and informational purposes</strong>. By using the Service, you acknowledge and agree to the following terms.
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-6">
          
          {/* Section 1 */}
          <div className="bg-black/70 backdrop-blur-sm border border-zinc-800 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-orange-400 mb-4 flex items-center gap-2">
              <span className="text-2xl">1.</span> No Operational Control Over Machinery
            </h3>
            <div className="text-zinc-300 space-y-3">
              <p><strong>CNC-Helpdesk:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Does <strong>NOT</strong> control, activate, or interface with CNC machines, laser cutters, or 3D printers</li>
                <li>Does <strong>NOT</strong> generate live commands for any hardware</li>
                <li>Does <strong>NOT</strong> connect to or operate machinery in real time</li>
              </ul>
              <p className="mt-4 text-yellow-400 font-medium">All content is offline guidance only, and the user is solely responsible for how they apply the information.</p>
            </div>
          </div>

          {/* Section 2 */}
          <div className="bg-black/70 backdrop-blur-sm border border-zinc-800 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-orange-400 mb-4 flex items-center gap-2">
              <span className="text-2xl">2.</span> User Responsibility & Required Competence
            </h3>
            <div className="text-zinc-300 space-y-3">
              <p>Operating CNC machinery — including routers, 3D printers, and laser cutters — involves <strong>inherent risks</strong>.</p>
              <p className="font-medium">By using this Service, you agree that:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>You are <strong>solely responsible</strong> for understanding the safe operation of your equipment</li>
                <li>You will read and follow all manufacturer instructions, manuals, and safety guidelines for your specific machine</li>
                <li>You will comply with applicable safety laws, regulations, and workplace procedures</li>
                <li>You will <strong>NOT</strong> rely solely on CNC-Helpdesk for training or operational decisions</li>
              </ul>
              <p className="mt-4 bg-yellow-500/10 border border-yellow-500/30 p-4 rounded-lg text-yellow-300">
                <strong>The Service is NOT a substitute for professional instruction, certification, or hands-on training.</strong>
              </p>
            </div>
          </div>

          {/* Section 3 - Risks */}
          <div className="bg-black/70 backdrop-blur-sm border border-red-500/30 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-red-400 mb-4 flex items-center gap-2">
              <span className="text-2xl">3.</span> Risks Associated With CNC Machinery
            </h3>
            <div className="text-zinc-300 space-y-4">
              <p><strong>CNC equipment can cause:</strong></p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-center gap-2 bg-red-500/10 p-3 rounded-lg">
                  <Flame className="w-5 h-5 text-red-400" />
                  <span>Fire hazards</span>
                </div>
                <div className="flex items-center gap-2 bg-red-500/10 p-3 rounded-lg">
                  <Zap className="w-5 h-5 text-red-400" />
                  <span>Burns and heat-related injuries</span>
                </div>
                <div className="flex items-center gap-2 bg-red-500/10 p-3 rounded-lg">
                  <Eye className="w-5 h-5 text-red-400" />
                  <span>Eye damage from laser exposure</span>
                </div>
                <div className="flex items-center gap-2 bg-red-500/10 p-3 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  <span>Mechanical harm from moving parts</span>
                </div>
                <div className="flex items-center gap-2 bg-red-500/10 p-3 rounded-lg">
                  <Zap className="w-5 h-5 text-red-400" />
                  <span>Electrical risks</span>
                </div>
                <div className="flex items-center gap-2 bg-red-500/10 p-3 rounded-lg">
                  <Wind className="w-5 h-5 text-red-400" />
                  <span>Harmful fumes, particulates, or smoke</span>
                </div>
              </div>

              <p className="mt-4"><strong>Users must ensure:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Appropriate ventilation</li>
                <li>Personal protective equipment (PPE)</li>
                <li>Supervised machine operation</li>
                <li>Routine maintenance</li>
                <li>Proper emergency procedures</li>
              </ul>

              <p className="mt-4 text-red-400 font-bold">CNC-Helpdesk accepts NO responsibility for injuries or damages resulting from machine operation.</p>
            </div>
          </div>

          {/* Section 4 */}
          <div className="bg-black/70 backdrop-blur-sm border border-zinc-800 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-orange-400 mb-4 flex items-center gap-2">
              <span className="text-2xl">4.</span> Accuracy of Information
            </h3>
            <div className="text-zinc-300 space-y-3">
              <p>Although we strive to provide accurate, up-to-date guidance:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Settings, feeds, speeds, and parameters <strong>vary widely</strong> by machine, material, and tool</li>
                <li>All calculators and recommendations are <strong>estimates only</strong></li>
                <li>Users must <strong>verify all settings</strong> against their machine specifications</li>
              </ul>
              <p className="mt-4 text-yellow-400">The Service does not guarantee accuracy or suitability for any specific purpose.</p>
            </div>
          </div>

          {/* Section 5 */}
          <div className="bg-black/70 backdrop-blur-sm border border-zinc-800 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-orange-400 mb-4 flex items-center gap-2">
              <span className="text-2xl">5.</span> No Professional Advice
            </h3>
            <div className="text-zinc-300 space-y-3">
              <p>Content provided by CNC-Helpdesk does <strong>NOT</strong> constitute:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Engineering advice</li>
                <li>Manufacturing advice</li>
                <li>Safety certification</li>
                <li>Regulatory compliance guidance</li>
                <li>A guarantee of performance or results</li>
              </ul>
              <p className="mt-4">Users are responsible for seeking professional consultation when required.</p>
            </div>
          </div>

          {/* Section 6 */}
          <div className="bg-black/70 backdrop-blur-sm border border-zinc-800 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-orange-400 mb-4 flex items-center gap-2">
              <span className="text-2xl">6.</span> Limitation of Liability
            </h3>
            <div className="text-zinc-300 space-y-3">
              <p>To the fullest extent permitted by law, CNC-Helpdesk is <strong>NOT liable</strong> for:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Personal injury</li>
                <li>Equipment damage</li>
                <li>Operational errors</li>
                <li>Financial loss</li>
                <li>Material waste</li>
                <li>Fires, mechanical failures, or accidents</li>
                <li>Misuse or misinterpretation of educational content</li>
              </ul>
              <p className="mt-4 text-red-400 font-bold">Your use of the Service is entirely at your own risk.</p>
            </div>
          </div>

          {/* Section 7 - User Agreement */}
          <div className="bg-orange-500/10 border-2 border-orange-500/50 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-orange-400 mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6" />
              <span>7. User Agreement</span>
            </h3>
            <div className="text-zinc-300">
              <p className="text-lg">
                <strong>By accessing CNC-Helpdesk.com:</strong>
              </p>
              <p className="mt-4 text-orange-300 font-medium">
                You acknowledge that you understand the risks associated with CNC machinery and agree to use the educational content responsibly, safely, and at your own discretion.
              </p>
              <p className="mt-4 text-yellow-400">
                <strong>If you do not agree to these terms, please discontinue use of the Service.</strong>
              </p>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-black/70 backdrop-blur-sm border border-zinc-800 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-orange-400 mb-4">8. Contact</h3>
            <p className="text-zinc-300">
              For questions or concerns regarding this Safety Disclaimer, please contact:<br />
              <a href="mailto:Andre@cnc-helpdesk.com" className="text-orange-500 hover:underline font-medium">Andre@cnc-helpdesk.com</a>
            </p>
          </div>
        </div>

        <p className="text-sm text-zinc-500 mt-8 text-center">
          Last Updated: 10 February 2026
        </p>
      </div>
    </div>
  );
};

export default SafetyDisclaimer;
